export type BrowserPreferences = {
  selectedOrganizationId?: string | null;
  selectedProjectId?: string | null;
  selectedEnvironmentId?: string | null;
  overviewTimeRange?: '15m' | '1h' | '24h' | '7d' | '14d' | 'all';
  feedbackViewMode?: 'cards' | 'table';
  consentReviewQueueViewMode?: 'cards' | 'table';
  feedbackFilterLabelMode?: 'icons' | 'labels' | 'both';
  feedbackSortMode?:
    | 'created_desc'
    | 'created_asc'
    | 'updated_desc'
    | 'updated_asc'
    | 'status'
    | 'status_desc'
    | 'priority'
    | 'priority_desc'
    | 'comments_desc'
    | 'errors_desc'
    | 'title_asc'
    | 'title_desc';
  engageAnalyticsTab?: 'overview' | 'feedback' | 'product' | 'trends';
  usageWindow?: '7d' | '28d' | '90d' | '180d' | '365d';
  usageIncludeTeam?: boolean;
  productBoardViewMode?: 'cards' | 'table';
  productRoadmapViewMode?: 'board' | 'gantt';
  productTableLayout?: {
    order?: string[];
    visible?: string[];
    widths?: Record<string, number>;
  };
  notificationsFilter?: 'all' | 'unread' | 'acknowledged' | 'dismissed';
  themeMode?: 'system' | 'light' | 'dark';
};

export const BROWSER_PREFERENCES_STORAGE_KEY = 'perspectify:browser-preferences';

function readStoredValue(key: string): string | null {
  if (typeof window === 'undefined') {
    return null;
  }

  return window.localStorage.getItem(key);
}

export function readBrowserPreferences(): BrowserPreferences {
  if (typeof window === 'undefined') {
    return {};
  }

  let storedPreferences: BrowserPreferences = {};

  try {
    storedPreferences = JSON.parse(
      window.localStorage.getItem(BROWSER_PREFERENCES_STORAGE_KEY) ?? '{}'
    ) as BrowserPreferences;
  } catch {
    storedPreferences = {};
  }

  return {
    selectedOrganizationId:
      storedPreferences.selectedOrganizationId ??
      readStoredValue('perspectify:selected-organization'),
    selectedProjectId:
      storedPreferences.selectedProjectId ?? readStoredValue('perspectify:selected-project'),
    selectedEnvironmentId: storedPreferences.selectedEnvironmentId ?? null,
    overviewTimeRange: storedPreferences.overviewTimeRange ?? '24h',
    feedbackViewMode: storedPreferences.feedbackViewMode ?? 'table',
    consentReviewQueueViewMode: storedPreferences.consentReviewQueueViewMode ?? 'cards',
    feedbackFilterLabelMode: storedPreferences.feedbackFilterLabelMode ?? 'both',
    feedbackSortMode: storedPreferences.feedbackSortMode ?? 'priority',
    engageAnalyticsTab: storedPreferences.engageAnalyticsTab ?? 'overview',
    usageWindow: storedPreferences.usageWindow ?? '28d',
    usageIncludeTeam: storedPreferences.usageIncludeTeam ?? false,
    productBoardViewMode: storedPreferences.productBoardViewMode ?? 'table',
    productRoadmapViewMode: storedPreferences.productRoadmapViewMode ?? 'board',
    productTableLayout: storedPreferences.productTableLayout,
    notificationsFilter: storedPreferences.notificationsFilter ?? 'unread',
    themeMode: storedPreferences.themeMode ?? 'system',
  };
}

export function readBrowserPreference<K extends keyof BrowserPreferences>(
  key: K
): BrowserPreferences[K] | null {
  return readBrowserPreferences()[key] ?? null;
}

export function writeBrowserPreference<K extends keyof BrowserPreferences>(
  key: K,
  value: BrowserPreferences[K]
): void {
  if (typeof window === 'undefined') {
    return;
  }

  const preferences = readBrowserPreferences();
  preferences[key] = value;
  window.localStorage.setItem(BROWSER_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));

  if (key === 'selectedOrganizationId') {
    window.localStorage.removeItem('perspectify:selected-organization');
  }
  if (key === 'selectedProjectId') {
    window.localStorage.removeItem('perspectify:selected-project');
  }
}

export function removeBrowserPreference<K extends keyof BrowserPreferences>(key: K): void {
  if (typeof window === 'undefined') {
    return;
  }

  const preferences = readBrowserPreferences();
  delete preferences[key];
  window.localStorage.setItem(BROWSER_PREFERENCES_STORAGE_KEY, JSON.stringify(preferences));
}
